<script>
// Generic grouped bar chart: one bar group per label, one dataset (color) per
// series — used by the transit-time report for province × month and district ×
// month averages. Unlike ExternalDailyBarChart this takes MULTIPLE datasets and
// can emit clicks so a page can drill into the clicked group.
import {Bar} from "vue-chartjs";

export default {
  extends: Bar,
  props: {
    labels: {type: Array, default: () => []},
    // [{label: '06/2026', color: '#2EA8D9', values: [..per label..]}, ...]
    datasets: {type: Array, default: () => []},
    unit: {type: String, default: ''},
    clickable: {type: Boolean, default: false},
    // Optional, same shape as datasets' values: extra tooltip line per bar
    // (the transit report passes the arrival rate so a low bar on a recent
    // month is readable as "not everything arrived yet", not "we got faster").
    tooltipNotes: {type: Array, default: null},
    // Optional line overlays on the SAME axis as the bars — a 30-day average, a
    // threshold (the COD advance report). [{label, color, values, dashed}];
    // additive: the default draws nothing, existing pages are unchanged.
    lines: {type: Array, default: () => []},
    // Optional value formatter for the y-axis ticks and tooltip values (e.g. a
    // thousands separator for Kip amounts). Additive: null keeps the raw number.
    formatValue: {type: Function, default: null},
  },
  mounted() {
    this.draw();
  },
  watch: {
    labels: "scheduleDraw",
    datasets: "scheduleDraw",
    lines: "scheduleDraw",
  },
  methods: {
    fmt(value) {
      if (value === null || value === undefined || value === '-') return value;
      return this.formatValue ? this.formatValue(value) : value;
    },
    // labels and datasets usually change in the same flush — one destroy +
    // recreate per tick instead of two.
    scheduleDraw() {
      if (this._drawPending) return;
      this._drawPending = true;
      this.$nextTick(() => {
        this._drawPending = false;
        this.draw();
      });
    },
    draw() {
      this.renderChart({
        labels: this.labels,
        datasets: this.datasets.map((set) => ({
          label: set.label,
          backgroundColor: set.color,
          // A real 0 value (e.g. same-day arrival average) must stay visible as
          // a sliver — only null ("no data") renders as nothing.
          minBarLength: 3,
          data: set.values,
        })).concat((this.lines || []).map((line) => ({
          type: 'line',
          label: line.label,
          borderColor: line.color || '#111827',
          backgroundColor: 'transparent',
          pointRadius: 0,
          fill: false,
          borderWidth: 2,
          borderDash: line.dashed === false ? [] : [6, 4],
          lineTension: 0,
          spanGaps: true,
          data: line.values,
        }))),
      }, {
        responsive: true,
        maintainAspectRatio: false,
        onClick: (evt, elements) => {
          if (this.clickable && elements && elements.length) {
            this.$emit('bar-click', {
              index: elements[0]._index,
              datasetIndex: elements[0]._datasetIndex,
            });
          }
        },
        onHover: (evt, elements) => {
          evt.target.style.cursor = this.clickable && elements.length ? 'pointer' : 'default';
        },
        scales: {
          yAxes: [{
            ticks: {
              beginAtZero: true,
              callback: (value) => this.fmt(value) + (this.unit ? ' ' + this.unit : ''),
              fontColor: 'black',
              fontFamily: 'Phetsarath OT',
              fontSize: 10,
            },
          }],
          xAxes: [{
            ticks: {
              fontColor: 'black',
              fontFamily: 'Phetsarath OT',
              fontSize: 10,
              maxRotation: 90,
              minRotation: 45,
              autoSkip: false,
            },
          }],
        },
        tooltips: {
          enabled: true,
          callbacks: {
            label: (item, data) => {
              const name = data.datasets[item.datasetIndex].label;
              const value = item.yLabel === null || item.yLabel === undefined ? '-' : this.fmt(item.yLabel);
              return name + ': ' + value + (this.unit ? ' ' + this.unit : '');
            },
            afterLabel: (item) => {
              const notes = this.tooltipNotes;
              if (!notes || !notes[item.datasetIndex]) return null;
              return notes[item.datasetIndex][item.index] || null;
            },
          },
          titleFontFamily: 'Phetsarath OT',
          bodyFontFamily: 'Phetsarath OT',
        },
        legend: {
          display: true,
          labels: {
            fontFamily: 'Phetsarath OT',
            fontColor: 'black',
          },
        },
        plugins: {
          // BarChart.vue registers chartjs-plugin-datalabels GLOBALLY — disable it
          // here (18 provinces × several months of labeled bars is pure noise).
          datalabels: {
            display: false,
          },
        },
      });
    },
  },
}
</script>
